import { withStyles } from '@material-ui/core/styles'
import { authorization } from 'Module/Auth/Auth'
import React, { useMemo, useState, Fragment } from 'react'
import { connect } from 'react-redux'
import { useParams } from 'react-router-dom'
import { CAMPAIGN_INTERNAL_STATUSES } from 'shared/constants'
import useGraphQLFetch from 'Utils/Hooks/useGraphQLFetch'
import { getClientCodesInfo } from '../../gql/getClientCodesInfo'
import { getInternalCodesInfo } from '../../gql/getInternalCodesInfo'
import AdminTool from '../AdminTool'
import ClientCodesTable from '../CodesTable/ClientCodesTable/ClientCodesTable'
import InternalCodesTable from '../CodesTable/InternalCodesTable/InternalCodesTable'
import ClientCodesButtons from '../CommonComponents/ClientCodesButtons'
import InternalCodesButtons from '../CommonComponents/InternalCodesButtons'
import Loader from '../CommonComponents/Loader'
import PageWrapper from '../CommonComponents/PageWrapper'
import OverrideCampaignStatusSection from '../OverrideCampaignStatusSection'
import RequiredDataTable from '../RequiredDataTable/RequiredDataTable'
import styles from './styles'
import CodeExplanation from '../CodeExplanation'

const AdminView = ({ classes, campaigns }) => {
  const { campaignId } = useParams()
  const campaign = campaigns[campaignId]
  const [clientCodesTable, setClientCodesTable] = useState(null)
  const [internalCodesTable, setInternalCodesTable] = useState(null)
  const [isClientCodesUploading, setIsClientCodesUploading] = useState(false)
  const [isInternalCodesUploading, setIsInternalCodesUploading] = useState(
    false,
  )

  const [clientCodesSelectedRows, setClientCodesSelectedRows] = useState([])
  const [internalCodesSelectedRows, setInternalCodesSelectedRows] = useState([])
  const [currentTrackingType, setCurrentTrackingType] = useState('BASIC')
  const [isDuplicateCodesExist, setIsDuplicateCodesExist] = useState(false)
  const [isDuplicateLinksExist, setIsDuplicateLinksExist] = useState(false)
  const [countCodesLinks, setCountCodesLinks] = useState({
    codes: {},
    links: {},
  })
  const [countInternalCodes, setCountInternalCodes] = useState({ codes: {} })
  const [showCodesExplanationPage, setShowCodesExplanationPage] = useState(
    false,
  )

  const selfServeData = campaign.selfServeData[0]

  const user = authorization.getUser()
  const isAdmin = user.role === 'ADMIN'
  const [internalStatus, setInternalStatus] = useState(
    selfServeData.internalStatus,
  )

  const isDynamicType = selfServeData.couponType === 'dynamic'
  const isHideRequiredData = !!(
    selfServeData.dynamicCouponCode || selfServeData.staticCouponCode
  )

  const isActionsDisabled =
    internalStatus !== CAMPAIGN_INTERNAL_STATUSES.CODE_REVIEW

  const isCodesUploadedAlready =
    internalStatus === CAMPAIGN_INTERNAL_STATUSES.CODE_REVIEW ||
    internalStatus === CAMPAIGN_INTERNAL_STATUSES.GENERATING_DIGITAL_PROOF ||
    internalStatus === CAMPAIGN_INTERNAL_STATUSES.PENDING_DIGITAL_PROOF ||
    internalStatus === CAMPAIGN_INTERNAL_STATUSES.PAYMENT ||
    internalStatus === CAMPAIGN_INTERNAL_STATUSES.ACTIVE ||
    internalStatus === CAMPAIGN_INTERNAL_STATUSES.COMPLETED

  const onClientCodesDeleted = () => {
    setClientCodesSelectedRows([])
    internalCodesRefetch({
      campaignId: campaignId,
    })
  }

  const onInternalCodesDeleted = () => {
    setInternalCodesSelectedRows([])
  }

  const mapClientCodesTable = (
    codesTable,
    trackingType = currentTrackingType,
  ) => {
    let isDuplicateCodesExist = false
    let isDuplicateLinksExist = false
    const countCodesLinks = {
      codes: {},
      links: {},
    }

    setClientCodesTable(codesTable)
    setCurrentTrackingType(trackingType)
    codesTable.forEach(row => {
      const code = row.clientCode
      const link = row.clientLink
      if (countCodesLinks.codes[code]) {
        countCodesLinks.codes[code] += 1
        isDuplicateCodesExist = true
      } else {
        countCodesLinks.codes[code] = 1
      }
      if (countCodesLinks.links[link]) {
        countCodesLinks.links[link] += 1
        isDuplicateLinksExist = true
      } else {
        countCodesLinks.links[link] = 1
      }
    })
    setIsDuplicateCodesExist(isDuplicateCodesExist)
    setIsDuplicateLinksExist(isDuplicateLinksExist)
    setCountCodesLinks(countCodesLinks)
    internalCodesRefetch({
      campaignId: campaignId,
    })
  }

  const mapInternalCodesTable = codesTable => {
    const countCodes = {
      codes: {},
    }
    setInternalCodesTable(codesTable)
    codesTable.forEach(row => {
      const code = row.internalCode
      if (countCodesLinks.codes[code]) {
        countCodesLinks.codes[code] += 1
      } else {
        countCodesLinks.codes[code] = 1
      }
    })
    setCountInternalCodes(countCodes)
  }

  const { isLoading, refetch: clientCodesRefetch } = useGraphQLFetch({
    fetchFn: getClientCodesInfo,
    defaultFetchArgs: {
      campaignId: campaignId,
    },
    mapResponse: data => {
      const codesTable = data.campaignClientCodes.clientCodes
      const trackingType = data.campaignClientCodes.trackingType
      mapClientCodesTable(codesTable, trackingType)
      setCurrentTrackingType(trackingType || 'BASIC')
    },
    skipFetchOnInit: false,
  })

  const {
    isLoading: internalCodesIsLoading,
    refetch: internalCodesRefetch,
  } = useGraphQLFetch({
    fetchFn: getInternalCodesInfo,
    defaultFetchArgs: {
      campaignId: campaignId,
    },
    mapResponse: data => {
      const codesTable = data.campaignInternalCodes.internalCodes
      mapInternalCodesTable(codesTable)
    },
    skipFetchOnInit: true,
  })

  const clientTableInfo = useMemo(() => {
    if (currentTrackingType) {
      return campaign.cityPlans.nodes
        .map(node => {
          if (node.missions.totalCount) {
            return {
              city: node.city.name,
              codesQuantity: (clientCodesTable || []).reduce((sum, code) => {
                if (code.city.id === node.city.id && code.clientCode) {
                  return sum + 1
                }
                return sum
              }, 0),
              linksQuantity: (clientCodesTable || []).reduce((sum, code) => {
                if (code.city.id === node.city.id && code.clientLink) {
                  return sum + 1
                }
                return sum
              }, 0),
              requiredQuantity:
                currentTrackingType === 'BASIC' ? 1 : node.missions.totalCount,
            }
          }
          return null
        })
        .filter(city => city)
    }
    return []
  }, [campaign, currentTrackingType, clientCodesTable])

  const internalTableInfo = useMemo(() => {
    if (currentTrackingType === 'BASIC' && internalCodesTable) {
      return campaign.cityPlans.nodes
        .map(node => {
          if (node.missions.totalCount) {
            return {
              city: node.city.name,
              codesQuantity: (internalCodesTable || []).reduce((sum, code) => {
                if (code.city.id === node.city.id && code.internalCode) {
                  return sum + 1
                }
                return sum
              }, 0),
              linksQuantity: (internalCodesTable || []).reduce((sum, code) => {
                if (code.city.id === node.city.id && code.internalCode) {
                  return sum + 1
                }
                return sum
              }, 0),
              requiredQuantity: node.missions.totalCount,
            }
          }
          return null
        })
        .filter(city => city)
    }
    return []
  }, [campaign, currentTrackingType, internalCodesTable])

  const isErrorExist =
    !!clientTableInfo.find(
      item =>
        item.requiredQuantity !== item.codesQuantity ||
        item.requiredQuantity !== item.linksQuantity,
    ) ||
    !!internalTableInfo.find(
      item =>
        item.requiredQuantity !== item.codesQuantity ||
        item.requiredQuantity !== item.linksQuantity,
    )

  const isWrongCodesQuantityInClientTableInfo =
    clientTableInfo.codesQuantity !== clientTableInfo.requiredQuantity ||
    clientTableInfo.linksQuantity !== clientTableInfo.requiredQuantity ||
    clientTableInfo.find(
      item =>
        item.requiredQuantity !== item.codesQuantity ||
        item.requiredQuantity !== item.linksQuantity,
    )

  if (showCodesExplanationPage) {
    return (
      <CodeExplanation onClose={() => setShowCodesExplanationPage(false)} />
    )
  }

  return (
    <div className={classes.wrapper}>
      {isLoading || internalCodesIsLoading ? <Loader /> : null}
      <PageWrapper>
        {!isDynamicType ? (
          <Fragment>
            <div className={classes.header}>
              <div className={classes.title}>Codes Uploaded by Client</div>
              <ClientCodesButtons
                mapCodesTable={mapClientCodesTable}
                setCodesTable={setClientCodesTable}
                isCodesUploading={isClientCodesUploading}
                setIsCodesUploading={setIsClientCodesUploading}
                selectedRows={clientCodesSelectedRows}
                onCodesDeleted={onClientCodesDeleted}
                isActionsDisabled={isActionsDisabled}
                onRefetch={internalCodesRefetch}
                setShowCodesExplanationPage={setShowCodesExplanationPage}
              />
            </div>

            {clientCodesTable && clientCodesTable.length ? (
              <div className={classes.table}>
                <ClientCodesTable
                  codes={clientCodesTable}
                  selectedRows={clientCodesSelectedRows}
                  setSelectedRows={setClientCodesSelectedRows}
                  countCodesLinks={countCodesLinks}
                  disabled={isActionsDisabled}
                />
              </div>
            ) : null}
          </Fragment>
        ) : null}

        {currentTrackingType === 'BASIC' &&
        clientCodesTable &&
        clientCodesTable.length &&
        internalCodesTable ? (
          <React.Fragment>
            <div className={classes.header}>
              <div className={classes.title}>
                Internal Flyer Codes ({isDynamicType ? 'Integration' : 'Admin'})
              </div>
              <InternalCodesButtons
                mapInternalCodesTable={mapInternalCodesTable}
                isCodesUploading={isInternalCodesUploading}
                setIsCodesUploading={setIsInternalCodesUploading}
                selectedRows={internalCodesSelectedRows}
                onCodesDeleted={onInternalCodesDeleted}
                isActionsDisabled={
                  isActionsDisabled || isWrongCodesQuantityInClientTableInfo
                }
                onRefetch={internalCodesRefetch}
                isCodesExist={internalCodesTable.length}
                campaignType={campaign.type}
              />
            </div>

            {internalCodesTable && internalCodesTable.length ? (
              <div className={classes.table}>
                <InternalCodesTable
                  codes={internalCodesTable}
                  selectedRows={internalCodesSelectedRows}
                  setSelectedRows={setInternalCodesSelectedRows}
                  countCodesLinks={countInternalCodes}
                  disabled={isActionsDisabled}
                />
              </div>
            ) : null}
          </React.Fragment>
        ) : null}
      </PageWrapper>
      <div className={classes.separator} />
      <PageWrapper>
        {!isHideRequiredData ? (
          <Fragment>
            <RequiredDataTable
              disabled={isActionsDisabled}
              tableInfo={clientTableInfo}
              title={'Required Data client'}
              isDuplicateCodesExist={isDuplicateCodesExist}
              isDuplicateLinksExist={isDuplicateLinksExist}
              isCodesUploadedAlready={isCodesUploadedAlready}
              currentTrackingType={currentTrackingType}
              isAdmin={isAdmin}
            />

            {currentTrackingType === 'BASIC' ? (
              <RequiredDataTable
                disabled={isActionsDisabled}
                tableInfo={internalTableInfo}
                title={'Required Data admin'}
                isDuplicateCodesExist={isDuplicateCodesExist}
                isDuplicateLinksExist={isDuplicateLinksExist}
                isCodesUploadedAlready={isCodesUploadedAlready}
                currentTrackingType={currentTrackingType}
                isAdmin={isAdmin}
              />
            ) : null}
          </Fragment>
        ) : null}
        <AdminTool
          isButtonsDisabled={
            isErrorExist ||
            internalStatus !== CAMPAIGN_INTERNAL_STATUSES.CODE_REVIEW
          }
          campaignStatus={internalStatus}
          setInternalStatus={setInternalStatus}
        />
        {internalStatus === CAMPAIGN_INTERNAL_STATUSES.UPLOAD_CODES ? (
          <OverrideCampaignStatusSection
            isButtonActive={true}
            setCampaignStatus={setInternalStatus}
            codesRefetch={async () => {
              await clientCodesRefetch({ campaignId })
              await internalCodesRefetch({ campaignId })
            }}
          />
        ) : null}
      </PageWrapper>
    </div>
  )
}

const mapStateToProps = (state, props) => ({
  campaigns: state.campaigns.campaigns,
  state: state,
})

export default connect(mapStateToProps)(withStyles(styles)(AdminView))
